import { useQuery, useMutation } from '@vue/apollo-composable';
import type {
  Announcement,
  Query,
  MutationAcknowledgeAnnouncementArgs,
  MutationDismissAnnouncementArgs,
} from '@/types/composition-functions';
import {
  ACTIVE_ANNOUNCEMENTS,
  ACKNOWLEDGE_ANNOUNCEMENT,
  DISMISS_ANNOUNCEMENT,
} from '@/graphql/announcements';

/**
 * Surfaces the school-wide announcements the backend already exposes via
 * `activeAnnouncements` and wires the `acknowledge` / `dismiss` mutations (#223).
 *
 * The `activeAnnouncements` list is server-scoped to the current user, so
 * persistence across reloads is server-driven: after acknowledging or dismissing
 * we refetch and trust whatever the server returns. `actedOn` is a session-local
 * optimistic hide so the UI updates instantly (and reconciles on the refetch);
 * it resets on reload, leaving the server as the source of truth.
 */
export function useAnnouncements() {
  const { result, loading, error, refetch } = useQuery<Pick<Query, 'activeAnnouncements'>>(
    ACTIVE_ANNOUNCEMENTS,
    undefined,
    { fetchPolicy: 'cache-and-network' },
  );

  const actedOn = ref<Set<string>>(new Set());

  const announcements = computed<Announcement[]>(
    () => (result.value?.activeAnnouncements ?? []).filter((a) => !actedOn.value.has(a.id)),
  );

  const hasAnnouncements = computed(() => announcements.value.length > 0);

  const { mutate: acknowledgeMutation } = useMutation<
    { acknowledgeAnnouncement: boolean }, MutationAcknowledgeAnnouncementArgs
  >(ACKNOWLEDGE_ANNOUNCEMENT);
  const { mutate: dismissMutation } = useMutation<
    { dismissAnnouncement: boolean }, MutationDismissAnnouncementArgs
  >(DISMISS_ANNOUNCEMENT);

  const hide = (id: string) => {
    actedOn.value = new Set(actedOn.value).add(id);
  };
  const unhide = (id: string) => {
    const next = new Set(actedOn.value);
    next.delete(id);
    actedOn.value = next;
  };

  const acknowledge = async(id: string) => {
    hide(id); // optimistic
    try {
      await acknowledgeMutation({ announcement_id: id });
      await refetch();
    } catch (e) {
      unhide(id); // roll back so the user can retry
      throw e;
    }
  };

  const dismiss = async(id: string) => {
    hide(id); // optimistic
    try {
      await dismissMutation({ announcement_id: id });
      await refetch();
    } catch (e) {
      unhide(id);
      throw e;
    }
  };

  return {
    announcements,
    hasAnnouncements,
    loading,
    error,
    refetch,
    acknowledge,
    dismiss,
  };
}
